import { Stack } from '@mui/material'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import TreatmentCreateDrawer from 'components/cattles/treatmentPage/components/drawers/TreatmentCreateDrawer'
import { pickTreatmentSchemaOptionsForDiagnoses } from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import AutocompleteInput from 'components/shared/textFields/AutocompleteInput'
import { useGetTreatmentSchemas } from 'hook/query/useGetTreatmentSchemas'
import { useGetTreatmentSchemasOptions } from 'hook/query/useGetTreatmentSchemasOptions'
import { ICheckupList, PositionHoof } from 'models'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { TreatmentSchemaDetails } from './TreatmentSchemaDetails'

interface TreatmentSchemsBlockProps {
	selectedHoof: PositionHoof
	setCheckupInfo: Dispatch<SetStateAction<ICheckupList>>
	checkupInfo: ICheckupList
	isSubmitAttempted?: boolean
}

export const TreatmentSchemsBlock = ({
	selectedHoof,
	setCheckupInfo,
	checkupInfo,
	isSubmitAttempted,
}: TreatmentSchemsBlockProps) => {
	const { data: treatmentSchemas = [] } = useGetTreatmentSchemas()
	const { data: schemaOptions = [], isLoading } =
		useGetTreatmentSchemasOptions()
	const diagnosesList =
		useAppSelector(state => state.cattlesSlice.diagnosesList) ?? []
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

	const selectedDiagnoses = useMemo(() => {
		const findElement = checkupInfo.diagnosis.find(
			diagnose => diagnose.position === selectedHoof,
		)
		return findElement?.sickness_id || []
	}, [checkupInfo, selectedHoof])

	const selectedSchema = useMemo(() => {
		const findElement = checkupInfo.diagnosis.find(
			diagnose => diagnose.position === selectedHoof,
		)
		return findElement?.treatment_plans_id || []
	}, [checkupInfo, selectedHoof])

	const treatmentOptions = useMemo(
		() =>
			pickTreatmentSchemaOptionsForDiagnoses(
				schemaOptions,
				treatmentSchemas,
				selectedDiagnoses,
				diagnosesList,
			),
		[schemaOptions, treatmentSchemas, selectedDiagnoses, diagnosesList],
	)

	const selectedSchemaDetails = useMemo(
		() => treatmentSchemas.filter(schema => selectedSchema.includes(schema.id)),
		[treatmentSchemas, selectedSchema],
	)

	const showSchemaError =
		Boolean(isSubmitAttempted) && selectedSchema.length === 0

	const handleSelectSchema = (hoof: PositionHoof, ids: number[]) => {
		setCheckupInfo(prev => {
			const updated = prev.diagnosis.map(diagnose =>
				diagnose.position === hoof
					? { ...diagnose, treatment_plans_id: ids }
					: diagnose,
			)

			const exists = prev.diagnosis.some(diagnose => diagnose.position === hoof)

			return {
				...prev,
				diagnosis: exists
					? updated
					: [
							...prev.diagnosis,
							{
								position: hoof,
								treatment_plans_id: ids,
								comment: '',
							},
						],
			}
		})
	}

	return (
		<Stack direction='column' gap='16px' sx={{ minWidth: 0, maxWidth: '100%' }}>
			<AutocompleteInput
				label='Выберите схему лечения'
				widthForInput='100%'
				isMultiple
				isCheckbox
				needChip
				isShowTags={false}
				loading={isLoading}
				isDisable={selectedDiagnoses.length === 0 && !showSchemaError}
				availableList={treatmentOptions}
				selectedValues={selectedSchema}
				isError={showSchemaError}
				helperText='Обязательное поле'
				onClick={(id: number) => {
					const isSelected = selectedSchema.includes(id)
					const newSelection = isSelected
						? selectedSchema.filter(d => d !== id)
						: [...selectedSchema, id]
					handleSelectSchema(selectedHoof, newSelection)
				}}
				startIcon={<SearchIcon stroke={COLORS.icon} />}
				onClickListButton={() => setIsModalOpen(prev => !prev)}
				textDescriptionForClickButton='Добавить новую схему лечения'
			/>
			<TreatmentSchemaDetails schemas={selectedSchemaDetails} />
			<TreatmentCreateDrawer
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>
		</Stack>
	)
}
